// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import * as Sentry from '@sentry/react';
import { ERROR_CODE_MAPPING } from '@/api/constants';
import { getUserFacingErrorMessage, handleAPIError } from '@/api/error-utils';
import { CancelChargeInput, cancelCharge } from '@/billing/api/cancel-charge';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

// User-facing status rejections routed to the caller via onError. Known
// codes skip Sentry — they're expected outcomes of racing state changes,
// not unexpected failures.
const EXPECTED_ERROR_CODES = new Set<number>([
  ERROR_CODE_MAPPING.charge_not_cancellable,
]);

interface UseCancelChargeOptions {
  onSuccess?: () => void;
  onError?: (
    message: string,
    apiStatusCode: number,
    httpStatusCode: number
  ) => void;
}

export function useCancelCharge(options?: UseCancelChargeOptions) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CancelChargeInput) => cancelCharge(input),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['charges'] });
      options?.onSuccess?.();
    },
    onError: async (error: AxiosError) => {
      const apiError = handleAPIError(error);
      const expected = EXPECTED_ERROR_CODES.has(apiError.apiStatusCode);
      if (!apiError.sentToSentry && !expected) {
        Sentry.captureException(error);
      }
      if (expected) {
        await queryClient.invalidateQueries({ queryKey: ['charges'] });
      }
      options?.onError?.(
        getUserFacingErrorMessage(apiError),
        apiError.apiStatusCode,
        apiError.httpStatusCode
      );
    },
  });
}
